import React, {forwardRef, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, useRecipe} from '@chakra-ui/react3'
import {useCurrentCustomer} from '../../../../../hooks/use-current-customer'
import {ACCOUNT_HREF, SIGN_IN_HREF} from '../../../../../constants'
import {HeaderUserIcon} from '../../../icons'
import {accountCtaRecipe} from './account-cta.recipe'
import HeaderIconButton from './header-icon-button'
import HeaderIconLink from './header-icon-link'

/**
 * Header account CTA.
 *
 * Renders the account icon with the shared `My Stokke` accessible name. By
 * default it links to the account page for a registered customer, or to sign-in
 * otherwise; pass `as` to render the same CTA chrome as another element, such
 * as the My Stokke menu button trigger.
 *
 * Rendered through `HeaderIconButton` for the round icon-button chrome
 * (36x36, hover, focus-ring); this component owns the user icon and logged-in
 * status dot.
 *
 * @component
 * @param {Object} props
 * @param {React.ElementType} [props.as] - Element or component rendered by `HeaderIconButton`.
 * @param {Object|Array} [props.css] - Extra Chakra `css` forwarded to the
 *   underlying `HeaderIconButton` (e.g. the header's responsive `account` slot
 *   that hides the CTA on mobile).
 * @param {string} [props.to] - Optional router destination when rendered as the default link.
 * @returns {JSX.Element}
 */
const AccountCta = forwardRef(({as, css, to, ...props}, ref) => {
    const {formatMessage} = useIntl()
    const {
        data: {isRegistered}
    } = useCurrentCustomer()

    const recipe = useRecipe({recipe: accountCtaRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    return (
        <HeaderIconButton
            as={as || HeaderIconLink}
            ref={ref}
            css={css}
            {...(as ? {} : {to: to || (isRegistered ? ACCOUNT_HREF : SIGN_IN_HREF)})}
            aria-label={formatMessage({
                id: 'header.button.assistive_msg.my_account',
                defaultMessage: 'My Stokke'
            })}
            {...props}
        >
            <HeaderUserIcon />
            {isRegistered ? <Box as="span" aria-hidden="true" css={styles.badge} /> : null}
        </HeaderIconButton>
    )
})

AccountCta.propTypes = {
    as: PropTypes.elementType,
    to: PropTypes.string,
    css: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
}

if (process.env.NODE_ENV !== 'production') {
    AccountCta.displayName = 'AccountCta'
}

export default AccountCta
